import { useTranslation } from "react-i18next";
import type { Column, Row, UseTableProps } from "../types/hooks";
import { useEffect, useState } from "react";
import {
  type RbContextMenuPosition,
  type RbRowSelectionModel,
} from "@scmlatam/web-ui";
import { useToolbar } from "./useToolbar";
import { Cell } from "../components/Cell";
import dayjs from "dayjs";

const useTable = ({
  setOpenUndoConfirm,
  setOpenDeleteConfirm,
}: UseTableProps) => {
  const { t } = useTranslation("execute_report.table");

  const toolbarProps = useToolbar();

  const columns: Column[] = [
    {
      key: "report_name",
      header: t("columns.report_name"),
      width: "23vw",
    },
    {
      key: "created_by",
      header: t("columns.created_by"),
      width: "15vw",
    },
    {
      key: "created_at",
      header: t("columns.created_at"),
      width: "15vw",
      render: (row) => <Cell t={t} row={row} column="created_at" />,
    },
    {
      key: "finished_at",
      header: t("columns.finished_at"),
      width: "15vw",
      render: (row) => <Cell t={t} row={row} column="finished_at" />,
    },
    {
      key: "state",
      header: t("columns.state.title"),
      width: "10vw",
      render: (row) => <Cell t={t} row={row} column="state" />,
    },
    {
      key: "format",
      header: t("columns.format"),
      width: "12vw",
      render: (row) => (
        <Cell
          t={t}
          row={row}
          column="format"
          canDeleteResult={toolbarProps.canDeleteResult}
          setOpen={setOpenUndoConfirm}
          setRowId={setRowId}
        />
      ),
    },
  ];

  const [rows, setRows] = useState<Row[]>([]);
  const [rowId, setRowId] = useState<string | null>(null);

  const [contextMenu, setContextMenu] = useState<RbContextMenuPosition | null>(
    null,
  );

  const [rowSelected, setRowSelected] = useState<RbRowSelectionModel>({
    ids: new Set(),
    type: "include",
  });

  const selectedIds =
    !Array.isArray(rowSelected) &&
      rowSelected &&
      typeof rowSelected === "object" &&
      "ids" in rowSelected
      ? rowSelected.ids
      : rowSelected;

  const selectedIdsSet = new Set(
    selectedIds instanceof Set
      ? Array.from(selectedIds)
      : Array.isArray(selectedIds)
        ? selectedIds
        : [],
  );

  const selectedRowIds =
    selectedIdsSet.size > 0
      ? selectedIdsSet
      : rowId
        ? new Set([rowId])
        : new Set<string>();

  useEffect(() => {
    // ordernar los resultados por created_at descendente usando dayjs
    setRows(
      toolbarProps.reportsResult.sort((a, b) =>
        dayjs(b.created_at).diff(dayjs(a.created_at)),
      ),
    );
  }, [toolbarProps.reportsResult]);

  return {
    ...toolbarProps,
    t,
    columns,
    rows,
    setRows,
    contextMenu,
    setContextMenu,
    rowSelected,
    setRowSelected,
    rowId,
    setRowId,
    selectedRowIds,
    setOpenDeleteConfirm,
  };
};

export { useTable };
